import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";

const destBadge = (d) =>
  d === "success-stories"
    ? "bg-royal/10 text-royal"
    : "bg-slate-100 text-slate-700";
const destLabel = (d) => (d === "success-stories" ? "Success Story" : "Insights");

export default function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 40);
    else { setQ(""); setItems([]); setActive(0); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!q.trim()) { setItems([]); return; }
    const h = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.get(`/admin/search`, { params: { q } });
        setItems(data.items || []);
        setActive(0);
      } catch { /* ignore */ }
      finally { setBusy(false); }
    }, 200);
    return () => clearTimeout(h);
  }, [q, open]);

  const go = (b) => {
    setOpen(false);
    nav(`/adminia/blogs/${b.id}/edit`);
  };

  const onKeyDown = (e) => {
    if (!items.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(items.length - 1, i + 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    if (e.key === "Enter") { e.preventDefault(); go(items[active]); }
  };

  return (
    <>
      <button
        data-testid="admin-global-search-trigger"
        onClick={() => setOpen(true)}
        title="Search (Ctrl/Cmd + K)"
        className="hidden md:inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-2 font-body text-xs text-slate-500 hover:border-navy hover:text-navy"
      >
        <Search className="h-3.5 w-3.5" /> Search articles
        <span className="ml-2 rounded bg-white border border-slate-200 px-1.5 py-0.5 font-mono text-[10px] text-slate-500">⌘K</span>
      </button>

      {open && (
        <div data-testid="admin-search-modal" className="fixed inset-0 z-50 flex items-start justify-center bg-navy/60 backdrop-blur-sm p-4 pt-24" onClick={() => setOpen(false)}>
          <div className="w-full max-w-2xl rounded-[1rem] border border-slate-200 bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-3">
              <Search className="h-5 w-5 text-slate-400" />
              <input
                ref={inputRef}
                data-testid="admin-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Search Insights, Journal, and Marketing…"
                className="w-full bg-transparent py-1 font-body text-base outline-none placeholder:text-slate-400"
              />
              <button onClick={() => setOpen(false)} className="rounded-full p-1.5 text-slate-500 hover:bg-slate-100"><X className="h-4 w-4" /></button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto p-2">
              {busy ? (
                <p className="p-6 font-body text-sm text-slate-500">Searching…</p>
              ) : !q.trim() ? (
                <p className="p-6 font-body text-sm text-slate-500">Type to search article titles, subtitles, excerpts, tags, and body content.</p>
              ) : items.length === 0 ? (
                <p className="p-6 font-body text-sm text-slate-500">No matches for "{q}".</p>
              ) : (
                items.map((b, i) => (
                  <button
                    key={b.id}
                    data-testid={`admin-search-result-${b.slug}`}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => go(b)}
                    className={`flex w-full items-start justify-between gap-3 rounded-lg p-3 text-left ${i === active ? "bg-slate-100" : "hover:bg-slate-50"}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-heading text-sm font-semibold text-navy">{b.title}</p>
                      {b.subtitle && <p className="mt-0.5 truncate font-body text-xs text-slate-500">{b.subtitle}</p>}
                      <div className="mt-1 flex items-center gap-2">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 font-body text-[10px] font-semibold ${destBadge(b.destination || "insights")}`}>{destLabel(b.destination || "insights")}</span>
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 font-body text-[10px] font-semibold ${b.status === "published" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{b.status}</span>
                        <span className="font-body text-[10px] text-slate-500">/{b.slug}</span>
                      </div>
                    </div>
                    <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 text-slate-400" />
                  </button>
                ))
              )}
            </div>
            <div className="border-t border-slate-100 px-4 py-2 font-body text-[11px] text-slate-500">
              <span className="mr-3">↑↓ navigate</span>
              <span className="mr-3">↵ open</span>
              <span>esc close</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
